import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, CircleDashed, Layers } from "lucide-react";
import { getHoldingProfile, setHoldingConsolidated } from "../../../../api/collections";
import { getHoldingPricing } from "../../../../api/billing";
import { showToast } from "../../../../utils/showToast";
import { Button } from "../../../../components/ui/Button";
import { Modal } from "../../../../components/ui/Modal";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Switch } from "../../../../components/ui/Switch";
import { apiErrorCode } from "../../cobro/utils/collectionReminders";
import { formatDate } from "../../cobro/utils/cobroHelpers";
import {
  consolidatedEnabledAtOf,
  consolidatedEnabledOf,
  holdingErrorText,
  holdingFieldSection,
  missingFieldsText,
} from "../../cobro/utils/holdings";

// Pestañas de la ficha donde se completa cada parte, en su orden (claves i18n).
const SECTION_LABELS = {
  fiscal: "Datos fiscales",
  contacto: "Contacto y plazo",
  tarifa: "Tarifa",
};
const SECTION_ORDER = ["fiscal", "contacto", "tarifa"];

// Tarifa propia VIGENTE hoy: una cuya primera versión empieza en una fecha futura todavía no
// cobra nada, así que no alcanza para encender.
const pricingReady = (view) => view?.exists === true && view?.effectiveVersionToday != null;

// Códigos faltantes agrupados por pestaña, en el orden de las pestañas; lo desconocido al final.
function groupBySection(fields) {
  const groups = new Map();
  for (const field of fields) {
    const section = holdingFieldSection(field);
    if (!groups.has(section)) groups.set(section, []);
    groups.get(section).push(field);
  }
  return [...SECTION_ORDER, null]
    .filter((section) => groups.has(section))
    .map((section) => ({ section, fields: groups.get(section) }));
}

/**
 * Interruptor "Cobro consolidado" del holding (dueño: billing, perfil de cobro del holding).
 *
 * Encendido, el holding absorbe a sus empresas: se cobran dentro de su nota consolidada, con su
 * tarifa, su contacto y su plazo, y el asistente de cobro ofrece el holding. Apagado (el
 * default), sus empresas se cobran sueltas, cada una con su contrato, y el holding no se ofrece.
 *
 * Encender exige que el holding esté listo. El checklist lo anticipa con lo que devuelven sus
 * dueños (datos fiscales de contracts; contacto, plazo y tarifa de billing) y deshabilita el
 * interruptor si ya se sabe que falta algo; la autoridad es billing, que responde 409
 * `holding_not_ready` con la lista de lo que falta. Apagar siempre se permite. Los dos sentidos
 * piden confirmación porque cambian a quién y con qué tarifa se cobra.
 *
 * @param {{ holding: object, refreshKey?: number,
 *           onGoToSection: (section: 'fiscal'|'contacto'|'tarifa') => void,
 *           onChanged?: (change: { holdingId: string, consolidatedEnabled: boolean,
 *                                  consolidatedEnabledAt: string|null }) => void }} props
 *   `holding` es un elemento de GET contracts/holdings (trae `missingFiscalFields`); cambiar
 *   `refreshKey` relee el perfil y la tarifa (p. ej. después de guardarlos en otra pestaña).
 */
export default function HoldingConsolidationSection({ holding, refreshKey = 0, onGoToSection, onChanged }) {
  const { t } = useTranslation();
  const blockedHintId = useId();
  const [profileView, setProfileView] = useState(null);
  const [pricingView, setPricingView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  // true = se pide confirmar encender; false = apagar; null = sin diálogo.
  const [confirming, setConfirming] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notReady, setNotReady] = useState(null);
  const [actionError, setActionError] = useState(null);
  const seq = useRef(0);
  const holdingId = String(holding.id);

  const load = useCallback(async () => {
    const current = ++seq.current;
    setLoading(true);
    const [profile, pricing] = await Promise.allSettled([
      getHoldingProfile(holdingId),
      getHoldingPricing(holdingId),
    ]);
    if (current !== seq.current) return;
    if (profile.status === "fulfilled") {
      setProfileView(profile.value?.data ?? null);
      setProfileError(null);
    } else {
      setProfileError(profile.reason);
    }
    if (pricing.status === "fulfilled") {
      setPricingView(pricing.value?.data?.data ?? null);
      setPricingError(null);
    } else {
      setPricingError(pricing.reason);
    }
    setLoading(false);
  }, [holdingId]);

  useEffect(() => {
    load();
    return () => {
      seq.current += 1;
    };
  }, [load, refreshKey]);

  // null = desconocido (no se pudo leer el perfil): no se ofrece cambiarlo a ciegas.
  const enabled = profileView ? consolidatedEnabledOf(profileView) : null;
  const enabledAt = profileView ? consolidatedEnabledAtOf(profileView) : null;
  const memberCount = Array.isArray(holding.companies) ? holding.companies.length : 0;
  const name = holding.name || t("Sin nombre");

  // `missing` null = no se pudo comprobar esa parte.
  const checklist = [
    {
      section: "fiscal",
      missing: Array.isArray(holding.missingFiscalFields) ? holding.missingFiscalFields : [],
    },
    { section: "contacto", missing: profileView ? profileView.missingFields ?? [] : null },
    {
      section: "tarifa",
      missing: pricingView ? (pricingReady(pricingView) ? [] : ["pricing"]) : null,
    },
  ];
  const knownMissing = checklist.some((item) => item.missing?.length > 0);
  const turnOnBlocked = enabled === false && knownMissing;

  const goTo = (section) => {
    if (section) onGoToSection?.(section);
  };

  const handleConfirm = async () => {
    const next = confirming;
    setSaving(true);
    setActionError(null);
    setNotReady(null);
    try {
      const { data } = await setHoldingConsolidated(holdingId, next);
      setProfileView(data);
      setConfirming(null);
      showToast(next ? t("Cobro consolidado encendido.") : t("Cobro consolidado apagado."), "success");
      onChanged?.({
        holdingId,
        consolidatedEnabled: consolidatedEnabledOf(data),
        consolidatedEnabledAt: consolidatedEnabledAtOf(data),
      });
    } catch (err) {
      setConfirming(null);
      if (apiErrorCode(err) === "holding_not_ready") {
        const missing = err?.response?.data?.missing;
        setNotReady(Array.isArray(missing) ? missing : []);
        // El checklist dijo que estaba listo: lo que se leyó quedó viejo.
        load();
      } else {
        setActionError(holdingErrorText(err, t, "No se pudo cambiar el cobro consolidado."));
      }
    } finally {
      setSaving(false);
    }
  };

  const sectionButton = (section) =>
    section && (
      <Button size="sm" variant="ghost" onClick={() => goTo(section)}>
        {t("Ir a {{section}}", { section: t(SECTION_LABELS[section]) })}
      </Button>
    );

  const stateText = (() => {
    if (enabled === true) {
      return enabledAt
        ? t("Encendido desde el {{date}}: sus empresas se cobran dentro de la nota consolidada del holding, con su tarifa, su contacto y su plazo.", {
            date: formatDate(enabledAt),
          })
        : t("Encendido: sus empresas se cobran dentro de la nota consolidada del holding, con su tarifa, su contacto y su plazo.");
    }
    if (enabled === false) {
      return t("Apagado: sus empresas se cobran sueltas, cada una con su contrato, y el asistente de cobro no ofrece este holding.");
    }
    return null;
  })();

  return (
    <SectionCard
      tone="bg"
      icon={<Layers className="h-4 w-4" />}
      title={t("Cobro consolidado")}
      subtitle={t("Una sola nota por período a nombre del holding, con el detalle de todas sus empresas.")}
      action={
        <Switch
          ariaLabel={t("Cobro consolidado")}
          checked={enabled === true}
          disabled={saving || enabled === null || turnOnBlocked}
          aria-describedby={turnOnBlocked ? blockedHintId : undefined}
          onChange={(next) => {
            setActionError(null);
            setConfirming(next);
          }}
        />
      }
    >
      <div className="space-y-3">
        {loading && !profileView && !profileError && (
          <p className="text-sm text-fg-muted" role="status">
            {t("Cargando…")}
          </p>
        )}
        {profileError && !profileView && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error"
            role="alert"
          >
            <span>
              {holdingErrorText(profileError, t, "No se pudo consultar el estado del cobro consolidado.")}
            </span>
            <Button size="sm" variant="outline" onClick={load}>
              {t("Reintentar")}
            </Button>
          </div>
        )}
        {stateText && <p className="text-sm text-fg">{stateText}</p>}

        {notReady && (
          <div className="rounded-md bg-status-error-soft p-3 text-sm text-status-error" role="alert">
            <p className="flex items-start gap-2 font-medium">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t("No se pudo encender: al holding le falta completar estos datos.")}
            </p>
            <ul className="mt-1 space-y-1">
              {groupBySection(notReady).map(({ section, fields }) => (
                <li key={section ?? "otros"} className="flex flex-wrap items-center gap-2">
                  <span>
                    {t("{{section}}: {{fields}}.", {
                      section: section ? t(SECTION_LABELS[section]) : t("Otros"),
                      fields: missingFieldsText(fields, t),
                    })}
                  </span>
                  {sectionButton(section)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {actionError && (
          <p className="text-sm text-status-error" role="alert">
            {actionError}
          </p>
        )}

        <div>
          <h4 className="text-sm font-semibold text-fg">{t("Lo que necesita la nota consolidada")}</h4>
          <ul className="mt-1 divide-y divide-border rounded-lg border border-border" aria-label={t("Lo que necesita la nota consolidada")}>
            {checklist.map(({ section, missing }) => {
              const ok = missing?.length === 0;
              const Icon = missing === null ? CircleDashed : ok ? CheckCircle2 : AlertTriangle;
              const tone = missing === null ? "text-fg-muted" : ok ? "text-status-success" : "text-status-warning";
              return (
                <li key={section} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <span className="flex min-w-0 items-start gap-2">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} aria-hidden />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-fg">{t(SECTION_LABELS[section])}</span>
                      <span className="block text-xs text-fg-muted">
                        {missing === null
                          ? t("No se pudo comprobar.")
                          : ok
                            ? t("Completo.")
                            : t("Falta: {{fields}}.", { fields: missingFieldsText(missing, t) })}
                      </span>
                    </span>
                  </span>
                  {!ok && sectionButton(section)}
                </li>
              );
            })}
          </ul>
          {turnOnBlocked && (
            <p id={blockedHintId} className="mt-1 text-xs text-fg-muted">
              {t("Completá lo que falta para poder encenderlo.")}
            </p>
          )}
          {!profileError && pricingError && (
            <p className="mt-1 text-xs text-fg-muted">
              {holdingErrorText(pricingError, t, "No se pudo cargar la tarifa del holding.")}
            </p>
          )}
        </div>
      </div>

      <Modal
        open={confirming !== null}
        onClose={() => {
          if (!saving) setConfirming(null);
        }}
        size="md"
        title={
          confirming
            ? t("¿Encender el cobro consolidado de {{name}}?", { name })
            : t("¿Apagar el cobro consolidado de {{name}}?", { name })
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirming(null)} disabled={saving}>
              {t("Cancelar")}
            </Button>
            <Button onClick={handleConfirm} loading={saving}>
              {confirming ? t("Sí, encender") : t("Sí, apagar")}
            </Button>
          </>
        }
      >
        {confirming ? (
          <div className="space-y-2 text-sm text-fg">
            <p>
              {t("Sus {{n}} empresas pasan a cobrarse dentro del holding: una sola nota consolidada por período, a nombre del holding, con su tarifa, su contacto y su plazo.", {
                n: memberCount,
              })}
            </p>
            <p>
              {t("Dejan de cobrarse sueltas: el asistente \"Cobrar a comercios\" ya no las ofrece por separado, solo a través del holding.")}
            </p>
          </div>
        ) : (
          <div className="space-y-2 text-sm text-fg">
            <p>
              {t("Sus {{n}} empresas vuelven a cobrarse sueltas, cada una con la tarifa, el plazo y los contactos de su propio contrato.", {
                n: memberCount,
              })}
            </p>
            <p>{t("El holding deja de ofrecerse en el asistente \"Cobrar a comercios\".")}</p>
          </div>
        )}
      </Modal>
    </SectionCard>
  );
}
